import React, { useState, useEffect } from 'react';
import { Target, Plus, Loader2, DollarSign, Calendar, Filter, Edit2, Trash2 } from 'lucide-react';
import { apiFetch } from '../../utils/api';

const OpportunitiesManagement: React.FC = () => {
    const [opportunities, setOpportunities] = useState<any[]>([]);
    const [accounts, setAccounts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editData, setEditData] = useState<any>(null);

    const [newOpp, setNewOpp] = useState({
        account_id: '', name: '', stage: 'Discovery', amount: '', currency: 'USD', probability: '20', expected_close_date: ''
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            const [oppsRes, accountsRes] = await Promise.all([
                apiFetch('/crm/opportunities'),
                apiFetch('/accounts')
            ]);
            if (oppsRes.ok) setOpportunities(await oppsRes.json());
            if (accountsRes.ok) setAccounts(await accountsRes.json());
        } catch (err) {
            console.error('Error fetching opportunities:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleAddOpportunity = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const res = await apiFetch('/crm/opportunities', {
                method: 'POST',
                body: JSON.stringify(newOpp)
            });
            if (res.ok) {
                setShowAddModal(false);
                setNewOpp({ account_id: '', name: '', stage: 'Discovery', amount: '', currency: 'USD', probability: '20', expected_close_date: '' });
                fetchData();
            }
        } catch (err) { console.error(err); }
    };

    const handleUpdateOpportunity = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editData) return;
        try {
            const res = await apiFetch(`/crm/opportunities/${editData.id}`, {
                method: 'PUT',
                body: JSON.stringify(editData)
            });
            if (res.ok) {
                setShowEditModal(false);
                fetchData();
            }
        } catch (err) { console.error(err); }
    };

    const handleDeleteOpportunity = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this opportunity?')) return;
        try {
            const res = await apiFetch(`/crm/opportunities/${id}`, { method: 'DELETE' });
            if (res.ok) fetchData();
        } catch (err) { console.error(err); }
    };

    const getStageColor = (stage: string) => {
        switch (stage) {
            case 'Closed Won': return 'bg-green-100 text-green-700';
            case 'Closed Lost': return 'bg-red-100 text-red-700';
            case 'Negotiation': return 'bg-orange-100 text-orange-700';
            default: return 'bg-blue-100 text-blue-700';
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <div className="flex gap-4">
                    <button className="flex items-center gap-2 px-4 py-2 border rounded-xl hover:bg-gray-50 transition-colors">
                        <Filter size={18} /> Filter
                    </button>
                    <div className="bg-gray-100 px-4 py-2 rounded-xl text-sm font-semibold text-gray-500">
                        Total Value: ${opportunities.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0).toLocaleString()}
                    </div>
                </div>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-xl hover:bg-blue-700 transition-all shadow-lg"
                >
                    <Plus size={20} /> Add Opportunity
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="animate-spin text-blue-500" size={40} /></div>
            ) : (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-100">
                                <th className="p-4 font-semibold text-gray-600">Opportunity Name</th>
                                <th className="p-4 font-semibold text-gray-600">Account</th>
                                <th className="p-4 font-semibold text-gray-600">Stage</th>
                                <th className="p-4 font-semibold text-gray-600">Value</th>
                                <th className="p-4 font-semibold text-gray-600">Prob.</th>
                                <th className="p-4 font-semibold text-gray-600">Expected Close</th>
                                <th className="p-4 font-semibold text-gray-600 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {opportunities.map((opp, i) => (
                                <tr key={i} className="hover:bg-blue-50/30 transition-colors group">
                                    <td className="p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
                                                <Target size={18} />
                                            </div>
                                            <span className="font-bold text-gray-800">{opp.name}</span>
                                        </div>
                                    </td>
                                    <td className="p-4 text-gray-600 font-medium">
                                        {accounts.find(a => a.id === opp.account_id)?.name || 'N/A'}
                                    </td>
                                    <td className="p-4">
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${getStageColor(opp.stage)}`}>
                                            {opp.stage}
                                        </span>
                                    </td>
                                    <td className="p-4 font-bold text-gray-800">
                                        {opp.currency} {Number(opp.amount).toLocaleString()}
                                    </td>
                                    <td className="p-4">
                                        <div className="flex items-center gap-2">
                                            <div className="w-12 h-2 bg-gray-100 rounded-full overflow-hidden">
                                                <div className="h-full bg-blue-500 rounded-full" style={{ width: `${opp.probability}%` }}></div>
                                            </div>
                                            <span className="text-xs font-bold text-gray-500">{opp.probability}%</span>
                                        </div>
                                    </td>
                                    <td className="p-4 text-gray-500 text-sm">
                                        <div className="flex items-center gap-2">
                                            <Calendar size={14} />
                                            {opp.expected_close_date ? new Date(opp.expected_close_date).toLocaleDateString() : 'TBD'}
                                        </div>
                                    </td>
                                    <td className="p-4 text-right">
                                        <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button onClick={() => { setEditData(opp); setShowEditModal(true); }} className="p-2 hover:bg-white rounded-lg transition-colors text-blue-600">
                                                <Edit2 size={16} />
                                            </button>
                                            <button onClick={() => handleDeleteOpportunity(opp.id)} className="p-2 hover:bg-white rounded-lg transition-colors text-red-600">
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {(showAddModal || showEditModal) && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-3xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        <h3 className="text-2xl font-bold text-gray-800 mb-6">{showEditModal ? 'Edit Opportunity' : 'Create New Opportunity'}</h3>
                        <form onSubmit={showEditModal ? handleUpdateOpportunity : handleAddOpportunity} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Account</label>
                                    <select
                                        className="w-full border p-3 rounded-xl bg-gray-50 outline-none focus:ring-2 focus:ring-blue-500/20"
                                        value={showEditModal ? editData?.account_id : newOpp.account_id}
                                        onChange={e => showEditModal ? setEditData({ ...editData, account_id: e.target.value }) : setNewOpp({ ...newOpp, account_id: e.target.value })}
                                        required
                                    >
                                        <option value="">Select Account</option>
                                        {accounts.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Opportunity Name</label>
                                    <input
                                        className="w-full border p-3 rounded-xl bg-gray-50 outline-none focus:ring-2 focus:ring-blue-500/20"
                                        value={showEditModal ? editData?.name : newOpp.name}
                                        onChange={e => showEditModal ? setEditData({ ...editData, name: e.target.value }) : setNewOpp({ ...newOpp, name: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Stage</label>
                                    <select
                                        className="w-full border p-3 rounded-xl bg-gray-50 outline-none focus:ring-2 focus:ring-blue-500/20"
                                        value={showEditModal ? editData?.stage : newOpp.stage}
                                        onChange={e => showEditModal ? setEditData({ ...editData, stage: e.target.value }) : setNewOpp({ ...newOpp, stage: e.target.value })}
                                    >
                                        <option value="Discovery">Discovery</option>
                                        <option value="Proposal">Proposal</option>
                                        <option value="Negotiation">Negotiation</option>
                                        <option value="Closed Won">Closed Won</option>
                                        <option value="Closed Lost">Closed Lost</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Expected Close</label>
                                    <input
                                        type="date"
                                        className="w-full border p-3 rounded-xl bg-gray-50 outline-none focus:ring-2 focus:ring-blue-500/20"
                                        value={showEditModal ? (editData?.expected_close_date || '').split('T')[0] : newOpp.expected_close_date}
                                        onChange={e => showEditModal ? setEditData({ ...editData, expected_close_date: e.target.value }) : setNewOpp({ ...newOpp, expected_close_date: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Amount</label>
                                    <div className="relative">
                                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                        <input
                                            type="number"
                                            className="w-full border pl-10 p-3 rounded-xl bg-gray-50 outline-none focus:ring-2 focus:ring-blue-500/20"
                                            value={showEditModal ? editData?.amount : newOpp.amount}
                                            onChange={e => showEditModal ? setEditData({ ...editData, amount: e.target.value }) : setNewOpp({ ...newOpp, amount: e.target.value })}
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Prob. (%)</label>
                                    <input
                                        type="number"
                                        className="w-full border p-3 rounded-xl bg-gray-50 outline-none focus:ring-2 focus:ring-blue-500/20"
                                        value={showEditModal ? editData?.probability : newOpp.probability}
                                        onChange={e => showEditModal ? setEditData({ ...editData, probability: e.target.value }) : setNewOpp({ ...newOpp, probability: e.target.value })}
                                        max="100" min="0"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-4 pt-4">
                                <button type="button" onClick={() => { setShowAddModal(false); setShowEditModal(false); }} className="flex-1 px-6 py-3 border border-gray-200 rounded-xl font-semibold hover:bg-gray-50 transition-colors">Cancel</button>
                                <button type="submit" className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all shadow-lg">
                                    {showEditModal ? 'Update Opportunity' : 'Create Opportunity'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OpportunitiesManagement;
